import {_state} from "../state/state.js";

let resizeObserverTimeout, scrollToElementTimeout;

let header = document.querySelector('#header');
let windowPosTop = 0;
let elPosTop = 0;
let scrollDuration = 0;
let headerHeight = 0;

let viewportChangeMobTab, viewportChangeTabDesk, viewportChangeMobDesk, viewportChangeAll;
viewportChangeMobTab = viewportChangeTabDesk = viewportChangeMobDesk = viewportChangeAll = false;

export const _utils = {
    init() {
        return new Promise( async (resolve, reject) => {
            await Promise.all([
                preloader(),
                this.loopVideos(),
                this.getScrollDirection(),
                this.setGlobalCssVars(),
                this.copyToClipboard(),
                this.wpcf7(),
                this.anchors(),
                this.customOrientationChange(),
            ]);
            resolve();
        })
    },
    initOnUserInteraction(){
        return new Promise(async (resolve, reject) => {
            await Promise.all([
                this.removeAttrTitle(),
                this.customCursor(),
            ]);
            resolve();
        });
    },

    //Global Css Vars
    setGlobalCssVars(){
        /**
         * This function allows setting global CSS custom properties (CSS variables).
         * You can add as many variables as needed within the function.
         * The variables are applied to the root element (document.documentElement - <html>).
         */
        header = document.querySelector('#header');
        return new Promise((resolve, reject) => {
            const set = () => {
                document.documentElement.style.setProperty('--window-width', `${window.innerWidth}px`);
                document.documentElement.style.setProperty('--window-height', `${window.innerHeight}px`);
                if(header){
                    document.documentElement.style.setProperty('--header-height',`${header.offsetHeight}px`);
                    document.documentElement.style.setProperty('--header-width',`${header.offsetWidth}px`);
                }
            }
            set();

            _utils.rebuildComponentsOnResize(() => {
                set();
            })

            resolve();
        })
    },

    //Custom Event "orientationchange"
    customOrientationChange(){
        return new Promise(async (resolve, reject) => {
            const mql = window.matchMedia("(orientation: portrait)");

            mql.addEventListener('change', (e) => {
                window.dispatchEvent(new Event('orientationchange'));
            });
            resolve();
        });
    },

    loopVideos() {
        /**
         * Loops videos with the 'data-loop' attribute.
         * This function listens for the 'ended' event on each video with the 'data-loop' attribute,
         * and when the video ends, it resets the current time to 0 and plays the video again.
         * This is necessary as the native 'loop' attribute doesn't trigger the 'ended' event.
         */
        return new Promise((resolve, reject) => {
            const videos = document.querySelectorAll('video[data-loop]');
            if (videos.length > 0) {
                for (const video of videos) {
                    video.addEventListener('ended', () => {
                        video.currentTime = 0;
                        video.play();
                    });
                }
            }
            resolve();
        })
    },
    removeAttrTitle(){
        /**
         * Removes the 'title' attribute from all elements on the page.
         * This function iterates over all elements that have a 'title' attribute,
         * removes it, and ensures that the tooltip (title) is not displayed on the page,
         * while keeping the attribute intact for search engine crawlers and robots.
         */
        return new Promise((resolve, reject) => {
            const els = document.querySelectorAll('[title]');
            if (els.length > 0) {
                for (const el of els) {
                    el?.removeAttribute('title');
                }
            }
            resolve();
        })
    },
    getScrollDirection(){
        /**
         * Tracks the scroll direction on the page and updates the state accordingly.
         * This function listens for the scroll event, calculates whether the user
         * is scrolling up or down, and updates the state with the corresponding direction:
         * 1 for scrolling down, 0 for scrolling up. The state is updated using _state.updateState().
         */
        return new Promise((resolve, reject) => {
            let lastScrollTop = window.pageYOffset || window.scrollY;

            window.addEventListener('scroll', () => {
                const currentScroll = window.scrollY;
                const direction = currentScroll > lastScrollTop ? 1 : 0;
                lastScrollTop = currentScroll <= 0 ? 0 : currentScroll;

                _state.updateState({ scrollDirection: direction });
            });
            resolve();
        })
    },

    //CF7
    wpcf7(){
        /**
         * This function manages the state of Contact Form 7 (CF7) forms by disabling or enabling the form interactions based on specific events.
         * It listens for different CF7 events to control the form's interactivity.
         *
         * - The form is disabled (preventing user interactions) on certain events like 'wpcf7spam' or 'wpcf7beforesubmit'.
         * - The form is enabled (allowing user interactions) after events like 'wpcf7invalid', 'wpcf7mailfailed', or when the form submission is successful and the 'wpcf7mailsent' event occurs.
         * - When the 'wpcf7mailsent' event is triggered, you can add custom classes to the form and set a custom timeout duration for re-enabling the form.
         *
         * The form's interactivity is controlled via the `pointerEvents` CSS property:
         * - 'none' disables interactions (e.g., form submission cannot be triggered again).
         * - An empty string (`''`) restores normal interactivity.
         */
        const disableForm = (form) => {
            form.style.pointerEvents = 'none';
        }
        const enableForm = (form) => {
            form.style.pointerEvents = '';
        }

        return new Promise((resolve, reject) => {
            const forms = document.querySelectorAll('form.wpcf7-form:not(.js-handled)');
            forms.forEach(form => {
                form.classList.add('js-handled');
                form.addEventListener('wpcf7mailsent', () => {
                    setTimeout(() => {
                        enableForm(form);
                    }, 6000)
                });

                const formEnableEvents = ['wpcf7invalid', 'wpcf7mailfailed'];
                formEnableEvents.forEach(event => {
                    form.addEventListener(event, () => {
                        enableForm(form);
                    });
                })

                const formDisableEvents = ['wpcf7spam', 'wpcf7beforesubmit'];
                formDisableEvents.forEach(event => {
                    form.addEventListener(event, () => {
                        disableForm(form);
                    });
                })
            });

            resolve();
        })
    },
    reInitCF7(){
        /**
         * This function reinitializes Contact Form 7 (CF7) forms that have not been initialized yet.
         * It checks for forms with the class `wpcf7-form` that haven't been handled (i.e., forms that do not have the `js-handled` class).
         *
         * - For each such form, it adds the class `js-handled` and triggers CF7 initialization using `window.wpcf7.init(form)`.
         * - This ensures that the form is properly initialized and ready for user interaction, even if it was dynamically added to the page.
         *
         * The function is useful when CF7 forms are dynamically loaded (e.g., via AJAX) and need to be initialized after the page load or form insertion.
         */
        const init = (form) => {
            if (window.wpcf7 && typeof window.wpcf7.init === 'function') {
                form.classList.add('js-handled-re-init')
                window.wpcf7.init(form);
            }
        }
        return new Promise(async (resolve, reject) => {
            const newForms = document.querySelectorAll('form.wpcf7-form:not(.js-handled-re-init)');
            if(newForms.length > 0){
                for(const form of newForms){
                    init(form);
                }
            }
            resolve();
        });
    },

    //Anchors
    anchors(){
        return new Promise((resolve, reject) => {
            const links = document.querySelectorAll('a:not(.js-handled)');
            links.forEach(link => {
                _utils.initAnchor(link);
            });

            resolve();
        });
    },
    anchorInUrl(){
        return new Promise((resolve, reject) => {
            try{
                if (window.location.hash && document.querySelector(`${window.location.hash}`)) {
                    _utils.scrollToElement(document.querySelector(`${window.location.hash}`));
                }
            } catch (error){

            }

            resolve();
        });
    },
    initAnchor(link){
        const href = link.href;
        if(!link.hasAttribute('role') && _utils.isSameDomain(href)){
            if(_utils.isValidURL(href) && !link.href.endsWith('#')){
                const hash = link.hash;
                try{
                    if(hash && document.querySelector(`${hash}`)){
                        link.classList.add('js-handled');
                        link.addEventListener('click', e => {
                            e.preventDefault();
                            _utils.scrollToElement(document.querySelector(`${hash}`));
                        });
                    }
                } catch (error){
                    link.style.pointerEvents = 'none';
                }
            } else {
                link.style.pointerEvents = 'none';
            }
        }
    },

    initSection(selector, callback){
        /**
         * Initializes unhandled sections matching the provided selector.
         *
         * - Selects all elements matching the given CSS selector.
         * - For each element that hasn't been handled (i.e., lacks the 'js-handled' class):
         *    - Adds the 'js-handled' class to mark it as processed.
         *    - If the element or one of its ancestors has a 'data-section' attribute:
         *        - Sets up one-time activation on user interaction (e.g., click, scroll) or custom "sVisible" event.
         *        - Once triggered, calls the provided callback with the section element and its index.
         *    - Otherwise, immediately invokes the callback with the section and index.
         *
         * @param {string} selector - CSS selector to target section elements.
         * @param {function} callback - Function to execute for each unhandled section. Receives (sectionElement, index).
         */
        const sections = document.querySelectorAll(selector);
        if (sections.length > 0 && typeof callback === 'function'){
            let index = 0;
            for(const section of sections){
                if(!section.classList.contains('js-handled')){
                    section.classList.add('js-handled');

                    const sectionIndex = index;
                    if(!!section.closest('[data-section]')){
                        let isStart = false;
                        const activate = () => {
                            if (!isStart) {
                                isStart = true;
                                callback(section, sectionIndex);
                                _state.interactionEvents.forEach(event => window.removeEventListener(event, activate));
                            }
                        };
                        _state.interactionEvents.forEach(event => window.addEventListener(event, activate));
                        if(section.closest('[data-section]').getBoundingClientRect().top < window.innerHeight){
                            section?.addEventListener("sVisible", activate);
                        }
                    } else {
                        callback(section, sectionIndex);
                    }
                }
                index++;
            }
        }
    },
    rebuildComponentsOnResize(callback, timeoutDelay = 500){
        /**
         * Rebuilds or reinitializes components when a window-related event is triggered.
         *
         * This function listens to multiple window events (e.g., 'resize', 'resizePage', 'orientationchange')
         * and executes a provided callback after a short debounce delay.
         *
         * Features:
         * - Skips execution if the callback is not a function.
         * - Ignores 'resize' events on mobile devices to avoid triggering on scroll-induced resizes.
         * - Executes only if the user is considered active (based on _state.isUserActive).
         * - Debounces the callback to avoid repeated rapid calls during continuous resizing.
         */
        if (typeof callback !== 'function') return false;

        let timeout;

        _state.windowResizeEvents.forEach(event => {
            window.addEventListener(event, () => {
                if(_state.isUserActive){
                    if (_utils.isMobile.any() && event === 'resize') return false;
                    clearTimeout(timeout);
                    timeout = setTimeout(() => {
                        callback();
                    }, timeoutDelay);
                }
            })
        });
    },

    detectBrowser() {
        /**
         * Detects browser and platform with focus on Safari / WebKit behavior.
         *
         * Returns:
         * - "safari"      → Desktop Safari (macOS)
         * - "ios_safari"  → iOS Safari / WebView
         * - "firefox"
         * - "chrome"
         * - "other"
         */
        const ua = navigator.userAgent;

        const isIOS = /iPad|iPhone|iPod/.test(ua);
        const isFirefox = /Firefox/i.test(ua);
        const isChrome = /Chrome|Chromium|CriOS/i.test(ua);
        const isSafari = /Safari/i.test(ua) && !isChrome && !isFirefox;

        if (isIOS && isSafari) return 'ios_safari';
        if (isSafari) return 'safari';
        if (isFirefox) return 'firefox';
        if (isChrome) return 'chrome';

        return 'other';
    },
    isElementHidden(el){
        /**
         * Checks if an element or any of its parent elements (except for html, body, and #page) is hidden.
         *
         * - The function traverses up the DOM tree starting from the given element and checks if any ancestor element has `display: none` or `visibility: hidden`.
         * - It stops and returns `true` if any parent element is hidden, except for the `html`, `body`, or `#page` elements.
         * - If no hidden elements are found (excluding the mentioned ones), it returns `false`.
         *
         * Parameters:
         * - `el`: The DOM element to start checking from.
         *
         * Returns:
         * - `true` if the element or any of its parents (except `html`, `body`, or `#page`) is hidden.
         * - `false` otherwise.
         */
        while (el) {
            // Skip checking for html, body, and #page elements
            if (el.tagName.toLowerCase() === 'html' || el.tagName.toLowerCase() === 'body' || el.id === 'page') {
                el = el.parentElement;
                continue;
            }

            // Check if the current element or its parent is hidden
            if (window.getComputedStyle(el).display === "none" || window.getComputedStyle(el).visibility === "hidden") {
                return true;
            }

            el = el.parentElement;
        }
        return false;
    },
    isNonEmptyObject(value) {
        /**
         * Checks if a value is a non-empty object.
         *
         * - The function checks if the given value is of type 'object' and not `null`.
         * - It also checks that the object has at least one property by verifying that the object contains keys.
         * - It returns `true` if the value is a non-empty object, otherwise `false`.
         *
         * Parameters:
         * - `value`: The value to be checked.
         *
         * Returns:
         * - `true` if the value is a non-empty object.
         * - `false` if the value is either not an object or an empty object.
         */
        return typeof value === 'object' && value !== null && Object.keys(value).length > 0;
    },
    isMobile: {
        // This object contains methods for detecting if the user's device is mobile or not based on the user agent string and other characteristics of the device.
        Android: () => /Android/i.test(navigator.userAgent),
        BlackBerry: () => /BlackBerry/i.test(navigator.userAgent),
        iOS: () => /iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.userAgent.includes("Macintosh") && 'ontouchend' in document),
        Opera: () => /Opera Mini/i.test(navigator.userAgent),
        Windows: () => /IEMobile|WPDesktop/i.test(navigator.userAgent),
        Surface: () => navigator.userAgent.includes('Windows') && 'ontouchstart' in window && navigator.maxTouchPoints > 1,
        any: function () {
            return this.Android() || this.BlackBerry() || this.iOS() || this.Opera() || this.Windows() || this.Surface() || !window.matchMedia("(hover: hover) and (pointer: fine)").matches;
        }
    },
    isValidURL(str) {
        /**
         * Validates if a given string is a valid URL.
         *
         * - The function attempts to create a new `URL` object from the provided string.
         * - If the string can be successfully parsed as a URL, the function returns `true`.
         * - If the string is not a valid URL and throws an error, the function returns `false`.
         *
         * Parameters:
         * - `str`: A string to be checked if it is a valid URL.
         *
         * Returns:
         * - `true` if the string is a valid URL.
         * - `false` if the string is not a valid URL.
         */
        try {
            new URL(str);
            return true;
        } catch (e) {
            return false;
        }
    },
    scrollToElement(el) {
        if (el) {
            clearTimeout(scrollToElementTimeout);
            _state.updateState({isScrollingToAnchor: true});
            header = document.querySelector('#header');
            windowPosTop = window.pageYOffset || window.scrollY;
            elPosTop = el.getBoundingClientRect().top;
            scrollDuration = Math.abs(Math.round(elPosTop));
            if (scrollDuration > 3000) {
                scrollDuration = 3000;
            } else if (scrollDuration < 300) {
                scrollDuration = 300;
            }
            if (header) headerHeight = header.getBoundingClientRect().height;

            if (elPosTop > 0) {
                if (_state.lenisInit !== null) {
                    _state.lenisInit.scrollTo(elPosTop + windowPosTop, {
                        duration: scrollDuration / 1000
                    });
                } else {
                    $("body, html").stop().animate({scrollTop: elPosTop + windowPosTop}, scrollDuration);
                }
            } else if (elPosTop < 0) {
                if (_state.lenisInit !== null) {
                    _state.lenisInit.scrollTo(elPosTop + windowPosTop - headerHeight, {
                        duration: scrollDuration / 1000
                    });
                } else {
                    $("body, html").stop().animate({scrollTop: elPosTop + windowPosTop - headerHeight}, scrollDuration);
                }
            }
            scrollToElementTimeout = setTimeout(() => {
                _state.updateState({isScrollingToAnchor: false});
            }, scrollDuration + 10)
        }
    },
    isSameDomain(url){
        try {
            const linkUrl = new URL(url, window.location.origin);
            return linkUrl.hostname === window.location.hostname;
        } catch (e) {
            console.error('Invalid URL:', e);
            return false;
        }
    },
    appendPathToUrl(url, extraPath){
        /**
         * Appends a path segment to the given URL if it is valid.
         * Returns the modified URL object or null if the input is not a valid URL.
         */
        if(_utils.isValidURL(url)){
            url = new URL(url);
            if(!url.pathname.endsWith('/')) url.pathname += '/';
            url.pathname += extraPath.replace(/^\/+/, '');
            return url;
        }
        return null;
    },
    getSiblings(e) {
        /**
         * Retrieves all sibling elements of a given element.
         *
         * - The function collects all the sibling elements of the provided element (`e`) that are of type element node.
         * - It checks the parent node and collects all siblings except the provided element itself.
         * - It returns an array containing all the sibling elements.
         *
         * Parameters:
         * - `e`: The target DOM element for which we want to get the siblings.
         *
         * Returns:
         * - An array of sibling elements (DOM nodes) of the provided element.
         */

            // for collecting siblings
        let siblings = [];
        // if no parent, return no sibling
        if (!e.parentNode) {
            return siblings;
        }
        // first child of the parent node
        let sibling = e.parentNode.firstChild;

        // collecting siblings
        while (sibling) {
            if (sibling.nodeType === 1 && sibling !== e) {
                siblings.push(sibling);
            }
            sibling = sibling.nextSibling;
        }
        return siblings;
    },
    sanitizeString(value) {
        /**
         * Sanitizes a string by removing potentially dangerous characters.
         *
         * This function removes characters that can be used in XSS attacks, such as:
         * - `<`, `>`, `/`, `"`, `'`, and backticks.
         *
         * This helps prevent Cross-Site Scripting (XSS) attacks by ensuring the string is safe for use in HTML contexts.
         *
         * Parameters:
         * - `value` (String): The string to sanitize.
         *
         * Returns:
         * - A sanitized string with dangerous characters removed.
         */
        return value.replace(/[<>\/\"'`]/g, '');
    },

    //----

    copyToClipboard(){
        /**
         * This function enables the functionality to copy text to the clipboard when a button with the class 'copy-to-clipboard' is clicked.
         * The button should have a 'data-copy' attribute containing the text to be copied.
         * The function adds a 'js-handled' class to ensure that the event handler is applied only once to each button.
         *
         * The clipboard operation uses the `navigator.clipboard.writeText` API, which only works when the page is served over HTTPS (SSL).
         * If the copying succeeds, an alert is shown with the copied text. If there's an error, it will be logged to the console.
         */
        return new Promise((resolve, reject) => {
            const buttons = document.querySelectorAll('.copy-to-clipboard:not(.js-handled)');
            buttons.forEach(btn => {
                btn.classList.add('js-handled');
                const textToCopy = btn.getAttribute('data-copy');
                btn.addEventListener('click', async () => {
                    if (!textToCopy) {
                        console.error('No text for copying');
                        return;
                    }

                    try {
                        await navigator.clipboard.writeText(textToCopy);
                        alert(`Copied: ${textToCopy}`);
                    } catch (err) {
                        console.error('Error when copying:', err);
                    }
                });
            });

            resolve();
        })
    },
    customCursor(){
        return new Promise((resolve, reject) => {
            const customCursorDefaultText = _state.scriptData && _state.scriptData.customCursorDefaultText ? _state.scriptData.customCursorDefaultText : 'Cursor Text';
            const elements = document.querySelectorAll('[data-custom-cursor]');
            const body = document.querySelector('#page') || document.body;
            if(elements.length > 0 && body){
                const cursor = document.createElement('div');
                const cursorSpan = document.createElement('span');
                cursorSpan.innerHTML = customCursorDefaultText;
                cursor.classList.add('cursor-drag');
                cursor.append(cursorSpan);
                body.prepend(cursor);

                const cursorCreate = {
                    delay: 2,
                    _x: 0,
                    _y: 0,
                    endX: (window.innerWidth / 2),
                    endY: (window.innerHeight / 2),
                    cursorVisible: true,
                    cursorEnlarged: false,
                    $outline: cursor,

                    init: function() {
                        this.outlineSize = this.$outline.offsetWidth;

                        if(!_utils.isMobile.any()){
                            this.setupEventListeners();
                            this.animateDotOutline();
                        }
                    },

                    setupEventListeners: function() {
                        let self = this;

                        document.addEventListener('mousemove', (e) => {
                            this.cursorPosition(e);
                        });
                        document.addEventListener('mousedown', (e) => {
                            this.cursorPosition(e);
                        });
                        document.addEventListener('wheel', (e) => {
                            const elementUnderCursor = document.elementFromPoint(e.clientX, e.clientY);
                            if (elementUnderCursor) {
                                if(elementUnderCursor.closest('[data-custom-cursor]')){
                                    self.$outline.classList.add('hover');
                                } else {
                                    self.$outline.classList.remove('hover');
                                }
                            }
                        });
                    },

                    animateDotOutline: function() {
                        let self = this;

                        self._x += (self.endX - self._x) / self.delay;
                        self._y += (self.endY - self._y) / self.delay;
                        self.$outline.style.top = self._y + 'px';
                        self.$outline.style.left = self._x + 'px';

                        requestAnimationFrame(this.animateDotOutline.bind(self));
                    },
                    cursorPosition: function(e, event = null){
                        let self = this;
                        self.endX = e.clientX;
                        self.endY = e.clientY;
                        if(e.target.closest('[data-custom-cursor]')){
                            self.$outline.classList.add('hover');
                        } else {
                            self.$outline.classList.remove('hover');
                        }
                    }
                }
                cursorCreate.init();
            }

            resolve();
        })
    },
}

export const observers = {
    //Custom Observers
    init(){
        return new Promise(async (resolve, reject) => {
            await Promise.all([
                this.initVideoAutoplayObserver(),
                this.initSectionObserver(),
                this.resizeObserver(),
            ]);
            resolve();
        });
    },

    initVideoAutoplayObserver() {
        return new Promise((resolve, reject) => {
            const videos = document.querySelectorAll('video[data-autoplay]');

            if (!videos.length) {
                resolve();
                return;
            }

            const observerOptions = {
                threshold: 0
            };

            const observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    const video = entry.target;

                    if (entry.isIntersecting && !video.hasAttribute('data-pause')) {
                        if (!video.hasAttribute('playsinline')) {
                            video.setAttribute('playsinline', '');
                        }
                        if (video.muted === false) {
                            video.muted = true;
                        }

                        video.play().then(() => {
                            video.parentElement.classList.add('v-reproduced');
                        }).catch(err => {
                            video.muted = true;
                            video.setAttribute('muted', '');
                            video.play().catch(error => {
                                console.warn(error);
                            });
                            document.addEventListener('click', () => {
                                video.muted = false;
                                video.removeAttribute('muted');
                            }, {once: true});
                        });
                    } else {
                        if (!video.paused && !video.ended && video.readyState > 2){
                            video.pause();
                        }
                    }
                });
            }, observerOptions);
            videos.forEach(video => observer.observe(video));
            document.addEventListener('visibilitychange', () => {
                if (document.hidden) {
                    videos.forEach(video => video.pause());
                } else {
                    videos.forEach(video => {
                        observer.unobserve(video);
                        observer.observe(video);
                    });
                }
            });
            resolve();
        })
    },
    initSectionObserver() {
        return new Promise((resolve, reject) => {
            const sections = document.querySelectorAll('[data-section]');
            const observers = new Map();

            sections.forEach(section => {
                const threshold = parseFloat(section.dataset.section) || 0;

                if (!observers.has(threshold)) {
                    observers.set(threshold, new IntersectionObserver((entries) => {
                        entries.forEach(entry => {
                            if (entry.isIntersecting) {
                                entry.target.classList.add('s-visible', 'is-viewed');
                                entry.target.dispatchEvent(new Event("sVisible"));
                            } else {
                                entry.target.classList.remove('s-visible');
                                entry.target.dispatchEvent(new Event("sHidden"));
                            }
                        });
                    }, { threshold }));
                }

                observers.get(threshold).observe(section);
            });

            resolve();
        })
    },
    resizeObserver() {
        return new Promise((resolve, reject) => {
            const resizeObserver = new ResizeObserver(entries => {
                for (let entry of entries) {
                    clearTimeout(resizeObserverTimeout);
                    resizeObserverTimeout = setTimeout(() => {
                        window.dispatchEvent(new Event("resizePage"));
                    }, 300)
                }
            });
            resizeObserver.observe(document.body);
            resolve();
        })
    },
}

export const _utilsSwiper = {
    duplicateSwiperSlides(slider, count = 3){
        /**
         * Duplicates slides in a Swiper slider to ensure it has a specific number of slides.
         *
         * - The function checks the number of slides in the given Swiper slider.
         * - If the total number of slides is less than the desired count, it duplicates the existing slides until the slider reaches the specified count.
         *
         * Parameters:
         * - `slider` (Element): The Swiper slider element to duplicate the slides in.
         * - `count` (Number, optional): The target number of slides to reach in the slider. Defaults to 3 if not provided.
         *
         * Functionality:
         * - It calculates how many times the existing slides need to be duplicated to meet or exceed the target `count`.
         * - Then, it loops through the slides, clones them, and appends them to the slider until the desired number of slides is reached.
         *
         * Example:
         * - If there are 4 slides, and `count = 10`, the function will duplicate the slides twice to ensure there are at least 10 slides in the slider.
         */
        const sliderItems = slider.querySelectorAll('.swiper-slide');
        const sliderWrapper = slider.querySelector('.swiper-wrapper');
        let slidesCount = sliderItems.length;

        if (slidesCount > 0 && slidesCount <= count) {
            let repeat = Math.ceil(count / slidesCount);
            if (repeat > 0) {
                for (let i = 0, n = repeat; i < n; i++) {
                    sliderItems.forEach((item) => {
                        const swiperItemNew = document.createElement("div");
                        swiperItemNew.classList.add('swiper-slide');
                        swiperItemNew.innerHTML = item.innerHTML;

                        sliderWrapper.append(swiperItemNew);
                    });
                }
            }
        }
    },
    stopSwiper(swiper){
        if(swiper){
            let transformValue;
            let slidePercent;
            let durationPercent;
            let translateX;
            let items = swiper.slides;
            const dir = swiper.el.getAttribute("dir");
            let matrix;
            let matrixValues;

            swiper.wrapperEl.addEventListener("mouseenter", function () {
                transformValue = window.getComputedStyle(swiper.wrapperEl).getPropertyValue("transform");
                matrix = transformValue.match(/^matrix\((.+)\)$/);
                if (matrix && matrix[1]) {
                    matrixValues = matrix[1].split(", ");
                    if (matrixValues.length >= 6) {
                        translateX = parseFloat(matrixValues[4]);
                        swiper.autoplay.stop();
                        transformValue = this.style.transform;
                        this.style.transitionDuration = "0ms";
                        this.style.transform = "translate3d(" + translateX + "px, 0px, 0px)";
                    } else {
                        console.error("Matrix transformation does not contain the expected values:", transformValue);
                    }
                } else {
                    console.error("Unable to extract translateX value from transform property:", transformValue);
                }

                items = swiper.slides;
                if (dir && dir === "rtl") {
                    slidePercent =
                        ((window.innerWidth - items[swiper.activeIndex].getBoundingClientRect().right) * 100) / (items[swiper.activeIndex].offsetWidth + swiper.params.spaceBetween);
                } else {
                    slidePercent = (items[swiper.activeIndex].getBoundingClientRect().left * 100) / (items[swiper.activeIndex].offsetWidth + swiper.params.spaceBetween);
                }
                durationPercent = ((swiper.params.speed * slidePercent) / 100);
            });

            swiper.wrapperEl.addEventListener("mouseleave", function () {
                this.style.transitionDuration = `${durationPercent}ms`;
                this.style.transform = transformValue;
                swiper.autoplay.start();
            });
        }
    },
}

export const viewportChecker = {
    beforeChange(){
        _state.updateState({isDesktop: window.matchMedia('(min-width: 1025px)').matches});
        _state.updateState({isTablet: !_state.isDesktop && window.matchMedia('(min-width: 768px)').matches});
        _state.updateState({isMobile: !_state.isDesktop && !_state.isTablet});
    },
    afterChange(){
        _state.updateState({prevDesktop: _state.isDesktop});
        _state.updateState({prevTablet: _state.isTablet});
        _state.updateState({prevMobile: _state.prevMobile});
    },

    mobTab(){
        viewportChangeMobTab = false;
        this.beforeChange();
        if (_state.isMobile !== _state.prevMobile || _state.isTablet !== _state.prevTablet){
            viewportChangeMobTab = true;
        }
        this.afterChange();
        return viewportChangeMobTab;
    },
    tabDesk(){
        viewportChangeTabDesk = false;
        this.beforeChange();
        if (_state.isDesktop !== _state.prevDesktop || _state.isTablet !== _state.prevTablet){
            viewportChangeTabDesk = true;
        }
        this.afterChange();
        return viewportChangeTabDesk;
    },
    mobDesk(){
        viewportChangeMobDesk = false;
        this.beforeChange();
        if (_state.isDesktop !== _state.prevDesktop || _state.isMobile !== _state.prevMobile){
            viewportChangeMobDesk = true;
        }
        this.afterChange();
        return viewportChangeMobDesk;
    },
    all(){
        viewportChangeAll = false;
        this.beforeChange();
        if (_state.isDesktop !== _state.prevDesktop || _state.isTablet !== _state.prevTablet || _state.isMobile !== _state.prevMobile){
            viewportChangeAll = true;
        }
        this.afterChange();
        return viewportChangeAll;
    },
}

export const urlUtils = {
    updateUrlParameter(param, paramVal) {
        /**
         * Updates the URL by adding or removing a query parameter without reloading the page.
         *
         * - The function modifies the current URL by either adding or updating the value of a specified query parameter (`param`).
         * - If the value of the parameter (`paramVal`) is provided and not empty, the parameter is set with the given value.
         * - If the value is `undefined` or an empty string, the parameter is removed from the URL.
         *
         * The URL is updated using the `replaceState` method of the `window.history` object, which ensures that the page does not reload when the URL is changed.
         *
         * Parameters:
         * - `param` (String): The name of the query parameter to update or remove.
         * - `paramVal` (String): The value to set for the query parameter. If omitted or an empty string is passed, the parameter is removed from the URL.
         *
         * Example:
         * - `updateUrlParameter('page', '2')`: Sets the `page` parameter to `2` in the current URL.
         * - `updateUrlParameter('page', '')`: Removes the `page` parameter from the URL.
         */
        const currentUrl = window.location.href;
        const newUrl = new URL(currentUrl);
        if (param !== 's'){
            if (paramVal !== undefined && paramVal !== '') {
                newUrl.searchParams.set(param, paramVal);
            } else {
                newUrl.searchParams.delete(param);
            }
        } else {
            newUrl.searchParams.set(param, paramVal);
        }
        history.replaceState(null, null, newUrl.toString());
    },
    getSafeQueryParam(param, defaultValue = null) {
        /**
         * Retrieves a query parameter from the URL and sanitizes its value for security.
         *
         * - This function fetches the value of a specified query parameter (`param`) from the current URL's search string.
         * - If the parameter exists, it returns the decoded value, sanitizing it by removing any potentially dangerous characters (`<`, `>`, `/`, `"`, `'`, and backticks).
         * - If the parameter is not found in the URL, it returns a default value (`defaultValue`), which is `null` by default.
         *
         * This helps prevent potential XSS (Cross-Site Scripting) attacks by removing unsafe characters from the query parameter's value.
         *
         * Parameters:
         * - `param` (String): The name of the query parameter to retrieve from the URL.
         * - `defaultValue` (Any): The default value to return if the parameter is not found in the URL (default is `null`).
         *
         * Example:
         * - `getSafeQueryParam('user', 'guest')`: Retrieves the `user` parameter from the URL or returns `'guest'` if it doesn't exist.
         */
        const urlParams = new URLSearchParams(window.location.search);
        const value = urlParams.get(param);

        if (value === null) return defaultValue;

        return _utils.sanitizeString(decodeURIComponent(value));
    },
}

export const CookieUtils = {
    set(name, value, days = 7, path = '/') {
        const expires = new Date(Date.now() + days * 864e5).toUTCString();
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=${path}`;
    },

    get(name) {
        const match = document.cookie.match(new RegExp('(?:^|; )' + encodeURIComponent(name) + '=([^;]*)'));
        return match ? decodeURIComponent(match[1]) : null;
    },

    remove(name, path = '/') {
        document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}`;
    },

    has(name) {
        return document.cookie.split('; ').some(cookie => cookie.startsWith(`${encodeURIComponent(name)}=`));
    }
};

export const LocalStorageUtils = {
    isSupported() {
        try {
            const testKey = '__test__';
            localStorage.setItem(testKey, '1');
            localStorage.removeItem(testKey);
            return true;
        } catch (e) {
            return false;
        }
    },

    set(key, value) {
        if (!this.isSupported()) return false;

        try {
            const json = JSON.stringify(value);
            localStorage.setItem(key, json);
            return true;
        } catch (e) {
            console.warn('Storage limit exceeded or value not serializable:', e);
            return false;
        }
    },

    get(key, defaultValue = null) {
        if (!this.isSupported()) return defaultValue;

        try {
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : defaultValue;
        } catch (e) {
            console.warn('Error parsing localStorage value:', e);
            return defaultValue;
        }
    },

    update(key, partialValue) {
        const current = this.get(key, {});
        const updated = { ...current, ...partialValue };
        return this.set(key, updated);
    },

    remove(key) {
        if (!this.isSupported()) return false;

        localStorage.removeItem(key);
        return true;
    },

    has(key) {
        if (!this.isSupported()) return false;
        return localStorage.getItem(key) !== null;
    }
};

export const preloader = () => {
    return new Promise((resolve, reject) => {
        const page = document.querySelector('#page');
        const preloader = document.querySelector('#preloader');
        setTimeout(() => {
            page?.classList.add('show');
        }, 0)
        preloader?.classList.add('start');
        if(preloader){
            setTimeout(() => {
                preloader.remove();
            }, 2000);
        }
        resolve();
    })
}